import Backoffice from "@snark/backoffice"
import BuildingCroppingField from "../components/BuildingCroppingField.vue"
import moment from "moment-timezone"

// noinspection JSUnusedLocalSymbols
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
	function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
	return new (P || (P = Promise))(function (resolve, reject) {
		function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
		function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
		function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
		step((generator = generator.apply(thisArg, _arguments || [])).next());
	});
};

const context = {
	account: Backoffice.populator("account", {
	}),
	vlbox: Backoffice.populator("vlbox", {
	}),
};

function buildDataUrlDescriptor(value, fallbackName = "mire") {
	if (typeof value !== "string" || !value.startsWith("data:")) return value;
	const mimeMatch = /^data:(.*?);base64,/.exec(value);
	const mimetype = mimeMatch ? mimeMatch[1] : "application/octet-stream";
	const ext = mimetype.includes("png") ? "png"
		: mimetype.includes("jpeg") ? "jpg"
		: mimetype.includes("mp4") ? "mp4"
		: mimetype.includes("quicktime") ? "mov"
		: "bin";
	return {
		path: value,
		mimetype,
		name: `${fallbackName}.${ext}`,
		preSignedUrl: ""
	};
}

function isS3Path(path) {
	if (!path || typeof path !== "string") return false;

	return (
		path.includes("presigned") ||
		path.includes("mire/per-vlbox")
	);
}

function buildStorageUrl(path) {
	if (!path) return "";

	const cleanPath = path.replace(/^\/+/, "");

	if (isS3Path(cleanPath)) {
		return `${process.env.VUE_APP_S3_URL}/${cleanPath}`;
	}

	return `${process.env.VUE_APP_API_STATIC_URL}/${cleanPath}`;
}

function getPictureUrl(value) {
	if (!value) return "";

	if (typeof value === "string") {
		return buildStorageUrl(value);
	}

	if (value.preSignedUrl) {
		return buildStorageUrl(value.preSignedUrl);
	}

	if (value.presignedUrl) {
		return buildStorageUrl(value.presignedUrl);
	}

	if (value.path) {
		return buildStorageUrl(value.path);
	}

	return "";
}

function getEditorFileUrl(propertyName, value) {
	if (!value) return "";

	if (value.preSignedUrl) {
		return buildStorageUrl(value.preSignedUrl);
	}

	if (value.presignedUrl) {
		return buildStorageUrl(value.presignedUrl);
	}

	if (
		value.path &&
		typeof value.path === "string" &&
		!value.path.startsWith("data:")
	) {
		return buildStorageUrl(value.path);
	}

	return "";
}

function formatPicture(field, item) {
	const value = item?.[field.name];
	const url = getPictureUrl(value);
	const separator = url.includes("?") ? "&" : "?";
	if (value?.mimetype && value.mimetype.startsWith("video/")) {
		return url
			? `<video src="${url}${separator}random=${Math.random().toString(36).slice(2)}" style="max-height: 300px; max-width: 100%;" controls muted playsinline crossorigin="anonymous"></video>`
			: "";
	}
	return url
		? `<img alt="" src="${url}${separator}random=${Math.random().toString(36).slice(2)}" style="max-height: 300px;" crossorigin="anonymous"/>`
		: "";
}

function findBuildingObject(component) {
	let current = component;
	while (current) {
		const object = current.object || current.$attrs?.object;
		if (object && object._id && Object.prototype.hasOwnProperty.call(object, "address")) {
			return object;
		}
		current = current.$parent;
	}
	return null;
}

function getVideoVlboxOptions(component) {
	const building = findBuildingObject(component);
	return context.vlbox.load().then(() => {
		const items = context.vlbox.items || [];
		return items
			.filter(v => v && v.mode === "video" && (!building?._id || String(v.buildingId) === String(building._id)))
			.map(v => ({
				value: v._id,
				label: `${v.name} (${v.model || v.brand || v._id})`
			}));
	});
}

export default {
	name: "building",
	collection: "building",
	idProperty: "_id",
	section: "app",
	backPopulateList: ["account", "vlbox"],
	labels: {"singleTitle":"Building","listTitle":"Buildings"},
	emptyItem: {"logActivation": false,"disabled": false,"volumeLevel": 30,"scenarioStartDelay": 0,"cronSendDeviceStatus": 1,"heartbeat": 5,"grace": 10,"selfUpdate":"0 3 * * *","systemMaintenance":"0 4 * * 0","location":{},"timezone":"Europe/Paris","picture":null,"mire":null,"mireMode":"perVlbox","mireFiles":[],"mires":[],"lastCommand":{"parameters":[],"response":null}},
	completeSingle(item) {
		// Les bâtiments créés avant l'ajout du champ "timezone" n'ont pas cette clé en base.
		// Elle doit exister dès le chargement (avant que Vue 2 ne rende l'objet réactif),
		// sinon la sélection dans le FieldSelect autocomplete ne se reflète jamais dans l'UI.
		if (!item.timezone) {
			item.timezone = "Europe/Paris";
		}
		return Promise.resolve(item);
	},
	fields: [
		{
			name: "_id",
			label: "Id",
			readOnly: true,
			hideOnCreate: true,
		},
		{
			name: "accountId",
			label: "Account Id",
			formatter: Backoffice.formatters.linkObjectId("account", {idProperty: "_id","label": context.account.formatter()}),
			editor: context.account.editor(),
		},
		{
			name: "name",
			label: "Name",
		},
		{
			name: "address",
			label: "Address",
		},
		{
			name: "location",
			label: "Location",
			children: [
				{
					name: "lat",
					label: "Lat",
					editor: {type: "float"},
				},
				{
					name: "lng",
					label: "Lng",
					editor: {type: "float"},
				},
			],
		},
		{
			name: "timezone",
			label: "Timezone",
			editor: {
				type: "select",
				autocomplete: true,
				options: () => Promise.resolve(moment.tz.names().map(name => ({value: name, label: name}))),
			},
		},
		{
			name: "picture",
			label: "Picture",
			formatter: formatPicture,
			editor: {
				type: "image",
				accept: "image/png,image/jpeg,image/webp",
				preview: {
					contain: true,
					formatUrl: (field, item) => getPictureUrl(item.value),
				}
			},
			noChildren: true,
		},
		{
			name: "mire",
			label: "Mire source (unique)",
			formatter: formatPicture,
			editor: {
				type: "file",
				accept: "image/png,image/jpeg,video/mp4,video/quicktime",
				addFileLabel: "Ajouter une mire source",
				formatUrl: getEditorFileUrl
			},
			noChildren: true,
		},
		{
			name: "mires",
			label: "Mires globales",
			list: true,
			children: [
				{
					name: "file",
					label: "Fichier",
					formatter: (field, item) => formatPicture(field, { [field.name]: item?.[field.name] }),
					editor: {
						type: "file",
						accept: "image/png,image/jpeg,video/mp4,video/quicktime",
						addFileLabel: "Ajouter une mire",
						formatUrl: getEditorFileUrl
					},
					noChildren: true,
				}
			]
		},
		{
			name: "mireMode",
			label: "Mode de mire",
			editor: {
				type: "select",
				options: [
					{ value: "perVlbox", label: "Association manuelle par VLBox" },
					{ value: "cropped", label: "Source unique avec découpage" }
				]
			}
		},
		{
			name: "mireFiles",
			label: "Mires par VLBox",
			list: true,
			children: [
				{
					name: "vlboxId",
					label: "VLBox",
					editor: {
						type: "select",
						options: getVideoVlboxOptions
					},
				},
				{
					name: "file",
					label: "Média",
					formatter: (field, item) => formatPicture(field, { [field.name]: item?.[field.name] }),
					editor: {
						type: "file",
						accept: "image/png,image/jpeg,video/mp4,video/quicktime",
						addFileLabel: "Ajouter un media",
						formatUrl: getEditorFileUrl
					},
					noChildren: true,
				}
			]
		},
		{
			name: "installedAt",
			label: "Installed At",
			formatter: Backoffice.formatters.date("DD/MM/YYYY"),
			editor: {type: "date"},
		},
		{
			name: "firstPlay",
			label: "First Play",
			formatter: Backoffice.formatters.date("DD/MM/YYYY"),
			editor: {type: "date"},
		},
		{
			name: "lastPlay",
			label: "Last Play",
			formatter: Backoffice.formatters.date("DD/MM/YYYY"),
			editor: {type: "date"},
		},
		{
			name: "lastStop",
			label: "Last Stop",
			formatter: Backoffice.formatters.date("DD/MM/YYYY"),
			editor: {type: "date"},
		},
		{
			name: "lastScenario",
			label: "Last Scenario",
		},
		{
			name: "currentScenario",
			label: "Current Scenario",
		},
		{
			name: "nextScenario",
			label: "Next Scenario",
		},
		{
			name: "nextPlay",
			label: "Next Play",
			formatter: Backoffice.formatters.date("DD/MM/YYYY"),
			editor: {type: "date"},
		},
		{
			name: "lastCommand",
			label: "Last Command",
			children: [
				{
					name: "id",
					label: "Id",
				},
				{
					name: "command",
					label: "Command",
				},
				{
					name: "timestamp",
					label: "Timestamp",
					formatter: Backoffice.formatters.date("DD/MM/YYYY"),
					editor: {type: "date"},
				},
				{
					name: "parameters",
					label: "Parameters",
					list: true,
				},
				{
					name: "status",
					label: "Status",
				},
				{
					name: "response",
					label: "Response",
				},
			],
		},
		{
			name: "limitStart",
			label: "Limit Start",
		},
		{
			name: "limitEnd",
			label: "Limit End",
		},
		{
			name: "scenarioStartDelay",
			label: "Délai lancement scénario (s)",
			editor: { type: "integer", min: 0 }
		},
		{
			name: "cronSendDeviceStatus",
			label: "Fréquence envoi status devices (min)",
			editor: { type: "integer"}
		},
		{
			name: "heartbeat",
			label: "Heartbeat devices (min)",
			editor: { type: "integer"}
		},
		{
			name: "grace",
			label: "Grace offline (min)",
			editor: { type: "integer"}
		},
		{
			name: "selfUpdate",
			label: "Self Update Cron",
			editor: { type: "text", placeholder: "0 3 * * *" }
		},
		{
			name: "systemMaintenance",
			label: "System Maintenance Cron",
			editor: { type: "text", placeholder: "0 4 * * 0" }
		},
		{
			name: "volumeLevel",
			label: "Volume Level",
			editor: { type: "integer", max: 100}
		},
		{
			name: "state",
			label: "State",
			readOnly: true,
			hideOnCreate: true,
			formatter: Backoffice.formatters.mapping({"deleted": "Deleted", "draft": "Draft"}),
		},
		{
			name: "logActivation",
			label: "Log Activation",
			editor: { type: "boolean" }
		},
		{
			name: "disabled",
			label: "Site désactivé",
			extra: "Bloque la programmation, la lecture manuelle et le warping/calibration sur ce site (l'upload de scénario reste possible).",
			editor: { type: "boolean" }
		},
		{
			name: "subscriptionStartAt",
			label: "Début de la période d'abonnement (personnalisé)",
			extra: "Pertinent uniquement si le compte associé a l'abonnement personnalisé activé. Laisser vide pour une année complète.",
			formatter: Backoffice.formatters.date("DD/MM/YYYY"),
			editor: { type: "date" }
		},
		{
			name: "subscriptionEndAt",
			label: "Fin de la période d'abonnement (personnalisé)",
			extra: "Pertinent uniquement si le compte associé a l'abonnement personnalisé activé. Laisser vide pour une année complète.",
			formatter: Backoffice.formatters.date("DD/MM/YYYY"),
			editor: { type: "date" }
		},
		{
			name: "brightness",
			label: "Luminosité (front)",
			editor: { type: "integer", min: -100, max: 100 }
		},
		{
			name: "contrast",
			label: "Contraste (front)",
			editor: { type: "integer", min: -100, max: 100 }
		},
		{
			name: "saturation",
			label: "Saturation (front)",
			editor: { type: "integer", min: -100, max: 100 }
		},
		{
			name: "enableCropping",
			label: "Découpage activé",
			editor: { type: "boolean" }
		},
		{
			name: "baseCroppingResolutionX",
			label: "Résolution base découpage X",
			editor: { type: "integer", min: 1 }
		},
		{
			name: "baseCroppingResolutionY",
			label: "Résolution base découpage Y",
			editor: { type: "integer", min: 1 }
		},
		{
			name: "cropping",
			label: "Zones de découpage",
			editor: {
				type: "custom",
				component: BuildingCroppingField,
			},
			noChildren: true,
		},
		{
			name: "createdAt",
			label: "Created At",
			readOnly: true,
			hideOnCreate: true,
			formatter: Backoffice.formatters.date("DD/MM/YYYY"),
		},
		{
			name: "updatedAt",
			label: "Updated At",
			readOnly: true,
			hideOnCreate: true,
			formatter: Backoffice.formatters.date("DD/MM/YYYY"),
		},
	],
	listColumns: [
		{
			name: "account",
			label: "Account",
			formatter: Backoffice.formatters.subProperty('name'),
			nosingle: true,
		},
		{
			name: "name",
			label: "Name",
		},
		{
			name: "address",
			label: "Address",
		},
	],
	loadSingle() {
		return Promise.all([
			context.account.load(),
			context.vlbox.load(),
		]);
	},
	beforeSave(item) {
		const cleaned = JSON.parse(JSON.stringify(item));
		if (!cleaned.mireMode) {
			cleaned.mireMode = Array.isArray(cleaned.mireFiles) && cleaned.mireFiles.length > 0 ? "perVlbox" : "cropped";
		}
		if (!cleaned.selfUpdate || !String(cleaned.selfUpdate).trim()) {
			cleaned.selfUpdate = "0 3 * * *";
		}
		if (!cleaned.systemMaintenance || !String(cleaned.systemMaintenance).trim()) {
			cleaned.systemMaintenance = "0 4 * * 0";
		}
		if (cleaned.mireMode !== "cropped") {
			cleaned.mire = null;
		}
		if (cleaned.mireMode !== "perVlbox") {
			cleaned.mireFiles = [];
		}
		if (cleaned.mire) {
			cleaned.mire = buildDataUrlDescriptor(cleaned.mire, "mire");
		}
		if (Array.isArray(cleaned.mireFiles)) {
			cleaned.mireFiles = cleaned.mireFiles
				.filter(entry => entry && entry.vlboxId && entry.file)
				.map(entry => ({
					...entry,
					file: buildDataUrlDescriptor(entry.file, `mire_${entry.vlboxId}`)
				}));
		}
		if (Array.isArray(cleaned.mires)) {
			cleaned.mires = cleaned.mires
				.filter(entry => entry && entry.file)
				.map((entry, i) => ({
					...entry,
					file: buildDataUrlDescriptor(entry.file, `mire_global_${i}`)
				}));
		}
		return Promise.resolve(cleaned);
	},
};
